import { apiClient } from "./client";
import type {
  FormFieldTemplate,
  FormFieldTemplateCreate,
  FormFieldTemplateUpdate,
  FormFieldTemplatesPaginated,
  FormFieldGroupTemplate,
  FormFieldGroupTemplateWithFields,
  FormFieldGroupTemplateCreate,
  FormFieldGroupTemplateUpdate,
  FormFieldGroupTemplatesPaginated,
  ApplyGroupTemplateResponse,
  ApplyFieldTemplateResponse,
  MessageResponse,
} from "@/types";

// ============== Form Field Group Templates ==============

interface ListGroupTemplatesParams {
  page?: number;
  page_size?: number;
  search?: string;
}

export async function listGroupTemplates(
  params: ListGroupTemplatesParams = {}
): Promise<FormFieldGroupTemplatesPaginated> {
  const response = await apiClient.get<FormFieldGroupTemplatesPaginated>(
    `/templates/groups`,
    { params }
  );
  return response.data;
}

export async function listAllGroupTemplatesWithFields(): Promise<
  FormFieldGroupTemplateWithFields[]
> {
  const response = await apiClient.get<FormFieldGroupTemplateWithFields[]>(
    `/templates/groups/all`
  );
  return response.data;
}

export async function getGroupTemplate(
  groupId: number
): Promise<FormFieldGroupTemplateWithFields> {
  const response = await apiClient.get<FormFieldGroupTemplateWithFields>(
    `/templates/groups/${groupId}`
  );
  return response.data;
}

export async function createGroupTemplate(
  data: FormFieldGroupTemplateCreate
): Promise<FormFieldGroupTemplate> {
  const response = await apiClient.post<FormFieldGroupTemplate>(
    `/templates/groups`,
    data
  );
  return response.data;
}

export async function updateGroupTemplate(
  groupId: number,
  data: FormFieldGroupTemplateUpdate
): Promise<FormFieldGroupTemplate> {
  const response = await apiClient.patch<FormFieldGroupTemplate>(
    `/templates/groups/${groupId}`,
    data
  );
  return response.data;
}

export async function deleteGroupTemplate(
  groupId: number
): Promise<MessageResponse> {
  const response = await apiClient.delete<MessageResponse>(
    `/templates/groups/${groupId}`
  );
  return response.data;
}

export async function duplicateGroupTemplate(
  groupId: number
): Promise<FormFieldGroupTemplate> {
  const response = await apiClient.post<FormFieldGroupTemplate>(
    `/templates/groups/${groupId}/duplicate`
  );
  return response.data;
}

export async function applyGroupTemplateToVersion(
  groupId: number,
  versionId: number,
  targetGroupId?: number
): Promise<ApplyGroupTemplateResponse> {
  const params = targetGroupId ? { target_group_id: targetGroupId } : {};
  const response = await apiClient.post<ApplyGroupTemplateResponse>(
    `/templates/groups/${groupId}/apply/${versionId}`,
    null,
    { params }
  );
  return response.data;
}

// ============== Form Field Templates (standalone) ==============

interface ListFieldTemplatesParams {
  page?: number;
  page_size?: number;
  search?: string;
  standalone_only?: boolean;
}

export async function listFieldTemplates(
  params: ListFieldTemplatesParams = {}
): Promise<FormFieldTemplatesPaginated> {
  const response = await apiClient.get<FormFieldTemplatesPaginated>(
    `/templates/fields`,
    { params }
  );
  return response.data;
}

export async function getFieldTemplate(
  fieldId: number
): Promise<FormFieldTemplate> {
  const response = await apiClient.get<FormFieldTemplate>(
    `/templates/fields/${fieldId}`
  );
  return response.data;
}

export async function createFieldTemplate(
  data: FormFieldTemplateCreate
): Promise<FormFieldTemplate> {
  const response = await apiClient.post<FormFieldTemplate>(
    `/templates/fields`,
    data
  );
  return response.data;
}

export async function updateFieldTemplate(
  fieldId: number,
  data: FormFieldTemplateUpdate
): Promise<FormFieldTemplate> {
  const response = await apiClient.patch<FormFieldTemplate>(
    `/templates/fields/${fieldId}`,
    data
  );
  return response.data;
}

export async function deleteFieldTemplate(
  fieldId: number
): Promise<MessageResponse> {
  const response = await apiClient.delete<MessageResponse>(
    `/templates/fields/${fieldId}`
  );
  return response.data;
}

export async function applyFieldTemplateToVersion(
  fieldId: number,
  versionId: number,
  targetGroupId: number
): Promise<ApplyFieldTemplateResponse> {
  const response = await apiClient.post<ApplyFieldTemplateResponse>(
    `/templates/fields/${fieldId}/apply/${versionId}`,
    null,
    { params: { target_group_id: targetGroupId } }
  );
  return response.data;
}
